// ThreeJs/Lidar/LidarMeshUtils.js
import * as THREE from "three";
import { sceneManager } from "../SetUps/SceneManager";
import { VARS } from "../Global/variables";

// keep your existing helper if you want (not required)
function isLidarObject(obj) {
  let cur = obj;
  while (cur) {
    if (cur.name === "lidarTerrainMesh" || cur.name === "lidarArrowControl") return true;
    cur = cur.parent;
  }
  return false;
}

// --- NEW: small helper to run worker once and return mesh buffers
let _lidarWorker = null;

function getLidarWorker() {
  if (_lidarWorker) return _lidarWorker;

  // Works in Vite / modern bundlers:
  _lidarWorker = new Worker(
    new URL("./LidarMeshWorker.js", import.meta.url),
    { type: "module" }
  );

  return _lidarWorker;
}

function buildMeshDataInWorker(lidarData) {
  return new Promise((resolve, reject) => {
    const worker = getLidarWorker();

    const onMessage = (e) => {
      const { ok, payload, error } = e.data || {};
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);

      if (!ok) reject(new Error(error || "Worker failed"));
      else resolve(payload);
    };

    const onError = (err) => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      reject(err);
    };

    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);

    worker.postMessage({ lidarData });
  });
}

export async function buildLidarMesh(lidarData) {
  if (!lidarData?.points?.length) return null;

  // Read saved LiDAR position (your existing logic)
  let savedLidarPos = null;
  try {
    if (VARS.Current_Project?.model_data) {
      const parsed = JSON.parse(VARS.Current_Project.model_data);
      savedLidarPos = parsed?.lidar?.position || null;
    }
  } catch (e) {
    console.warn("Failed to parse saved LiDAR position:", e);
  }

  // If already exists, remove old
  const existingMesh = sceneManager.scene.getObjectByName("lidarTerrainMesh");
  if (existingMesh) sceneManager.scene.remove(existingMesh);

  const existingArrow = sceneManager.scene.getObjectByName("lidarArrowControl");
  if (existingArrow) sceneManager.scene.remove(existingArrow);

  // ✅ Heavy work in worker
  const meshPayload = await buildMeshDataInWorker(lidarData);

  const positions = new Float32Array(meshPayload.positions);
  const colors = new Float32Array(meshPayload.colors);
  const indices = new Uint32Array(meshPayload.indices);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();

  const material = new THREE.MeshLambertMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.8,
    wireframe: false,
  });

  const terrainMesh = new THREE.Mesh(geometry, material);
  terrainMesh.name = "lidarTerrainMesh";

  // --- Arrow control (your existing structure, just uses worker-provided default position)
  const createArrowControl = () => {
    const arrowGroup = new THREE.Group();
    arrowGroup.name = "lidarArrowControl";

    const shaftGeometry = new THREE.CylinderGeometry(2, 2, 10, 8);
    const shaftMaterial = new THREE.MeshBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.8,
    });
    const shaft = new THREE.Mesh(shaftGeometry, shaftMaterial);
    shaft.name = "arrowShaft";
    shaft.rotation.x = Math.PI / 2;

    const headGeometry = new THREE.ConeGeometry(6, 20, 8);
    const headMaterial = new THREE.MeshBasicMaterial({
      color: 0xff3300,
      transparent: true,
      opacity: 0.9,
    });
    const head = new THREE.Mesh(headGeometry, headMaterial);
    head.name = "arrowHead";
    head.rotation.x = Math.PI / 2;
    head.position.z = 10;

    arrowGroup.add(shaft);
    arrowGroup.add(head);

    const ap = meshPayload.arrowPos || { x: 0, y: 0, z: 20 };
    arrowGroup.position.set(ap.x, ap.y, ap.z);

    return arrowGroup;
  };

  const arrowControl = createArrowControl();

  // --- Drag logic: keep your existing logic (copied with minimal changes)
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  let isDragging = false;
  let dragType = null;
  let dragStartX = 0;
  let dragStartY = 0;
  let initialMeshX = terrainMesh.position.x;
  let initialMeshY = terrainMesh.position.y;
  let initialMeshZ = terrainMesh.position.z;
  let initialArrowX = arrowControl.position.x;
  let initialArrowY = arrowControl.position.y;
  let initialArrowZ = arrowControl.position.z;

  const onMouseDown = (event) => {
    if (event.button !== 0) return;

    if (!terrainMesh.visible || !arrowControl.visible) return;
    event.preventDefault();

    const canvas = sceneManager.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, sceneManager.camera);

    const allHits = raycaster.intersectObjects(sceneManager.scene.children, true);
    if (!allHits.length) return;

    const topHitObj = allHits[0].object;
    if (!isLidarObject(topHitObj)) return;

    const arrowIntersects = raycaster.intersectObjects(arrowControl.children, true);
    if (arrowIntersects.length > 0) {
      isDragging = true;
      dragType = "arrow";
      dragStartY = event.clientY;
      initialMeshZ = terrainMesh.position.z;
      initialArrowZ = arrowControl.position.z;

      arrowControl.children.forEach((child) => {
        if (child.material) child.material.color.setHex(0x00ff00);
      });

      if (sceneManager.controls) sceneManager.controls.enabled = false;
      return;
    }

    const meshIntersects = raycaster.intersectObject(terrainMesh);
    if (meshIntersects.length > 0) {
      isDragging = true;
      dragType = "terrain";
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      initialMeshX = terrainMesh.position.x;
      initialMeshY = terrainMesh.position.y;
      initialArrowX = arrowControl.position.x;
      initialArrowY = arrowControl.position.y;

      canvas.style.cursor = "move";
      if (sceneManager.controls) sceneManager.controls.enabled = false;
    }
  };

  const onMouseMove = (event) => {
    if (event.buttons !== 1) return;

    if (!isDragging) {
      if (!terrainMesh.visible || !arrowControl.visible) return;

      const canvas = sceneManager.renderer.domElement;
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, sceneManager.camera);

      const meshIntersects = raycaster.intersectObject(terrainMesh);
      const arrowIntersects = raycaster.intersectObjects(arrowControl.children, true);

      if (arrowIntersects.length > 0) canvas.style.cursor = "ns-resize";
      else if (meshIntersects.length > 0) canvas.style.cursor = "move";
      else canvas.style.cursor = "default";

      return;
    }

    if (!terrainMesh.visible || !arrowControl.visible) {
      isDragging = false;
      dragType = null;
      const canvas = sceneManager.renderer.domElement;
      canvas.style.cursor = "default";
      if (sceneManager.controls) sceneManager.controls.enabled = true;
      return;
    }

    event.preventDefault();

    if (dragType === "arrow") {
      const deltaY = event.clientY - dragStartY;
      const sensitivity = 0.1;
      const zOffset = -deltaY * sensitivity;

      terrainMesh.position.z = initialMeshZ + zOffset;
      arrowControl.position.z = initialArrowZ + zOffset;
    } else if (dragType === "terrain") {
      const deltaX = event.clientX - dragStartX;
      const deltaY = event.clientY - dragStartY;
      const sensitivity = 0.1;

      const camera = sceneManager.camera;
      const right = new THREE.Vector3();
      const up = new THREE.Vector3();

      right.setFromMatrixColumn(camera.matrix, 0);
      up.setFromMatrixColumn(camera.matrix, 1);

      const movement = new THREE.Vector3();
      movement.addScaledVector(right, deltaX * sensitivity);
      movement.addScaledVector(up, -deltaY * sensitivity);

      terrainMesh.position.x = initialMeshX + movement.x;
      terrainMesh.position.y = initialMeshY + movement.y;

      arrowControl.position.x = initialArrowX + movement.x;
      arrowControl.position.y = initialArrowY + movement.y;
    }
  };

  const onMouseUp = (event) => {
    if (event.button !== 0) return;

    if (isDragging) {
      const canvas = sceneManager.renderer.domElement;

      if (dragType === "arrow") {
        arrowControl.children.forEach((child) => {
          if (child.material) {
            if (child.name === "arrowShaft") child.material.color.setHex(0xff6600);
            if (child.name === "arrowHead") child.material.color.setHex(0xff3300);
          }
        });
      }

      canvas.style.cursor = "default";
      isDragging = false;
      dragType = null;

      if (sceneManager.controls) sceneManager.controls.enabled = true;
    }
  };

  // listeners
  const canvas = sceneManager.renderer.domElement;
  canvas.addEventListener("mousedown", onMouseDown);
  canvas.addEventListener("mousemove", onMouseMove);
  canvas.addEventListener("mouseup", onMouseUp);

  // add objects
  sceneManager.scene.add(terrainMesh);
  sceneManager.scene.add(arrowControl);

  terrainMesh.visible = true;
  arrowControl.visible = true;

  // Apply saved pos (your existing behavior)
  if (savedLidarPos) {
    terrainMesh.position.set(savedLidarPos.x || 0, savedLidarPos.y || 0, savedLidarPos.z || 0);

    arrowControl.position.x += savedLidarPos.x || 0;
    arrowControl.position.y += savedLidarPos.y || 0;
    arrowControl.position.z += savedLidarPos.z || 0;
  }

  // cleanup
  return () => {
    canvas.removeEventListener("mousedown", onMouseDown);
    canvas.removeEventListener("mousemove", onMouseMove);
    canvas.removeEventListener("mouseup", onMouseUp);

    sceneManager.scene.remove(terrainMesh);
    sceneManager.scene.remove(arrowControl);

    geometry.dispose();
    material.dispose();
    arrowControl.children.forEach((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });
  };
}
